import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  createEmptyShot,
  isDirectUploadService,
  PORTRAIT_MAX_COUNT,
  ShotFlow,
  shotImageIds,
  sortServicesByTabOrder,
} from "@beleef/editor-shot";
import axios from "utils/axios";
import { FaFileUpload, FaArrowLeft } from "react-icons/fa";
import { FiFolderPlus } from "react-icons/fi";
import { HiChevronRight } from "react-icons/hi2";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import { showToast } from "components/ui/toast";
import { useRawImageConversion } from "apis/public";
import { useDownloadManager } from "hooks/useDownloadManager";
import DownloadProgressToast from "components/ui/DownloadProgressToast";
import ImageContainer from "components/upload/ImageContainer";
import Lightbox from "components/ui/Lightbox";
import HelpGuide from "components/ui/HelpGuide";
import {
  useAdminNotifyEditorMutation,
  useAdminSaveImagesMutation,
  useAdminDeleteS3FileMutation,
  useCreateEditorShotMutation,
} from "apis/prepareMarketing";
import {
  contractorDeliversEditedPhotos,
  requiredEditedCount,
  folderField,
} from "utils/editedPhotoContractor";
/**
 * Admin ServiceImageUpload — manages ALL photo-type services for one event.
 * Same architecture as contractor ServiceImageUpload but with:
 *   - /admin/ API routes
 *   - useRawImageConversion for CR3 rendering (admin approach)
 *   - Extras folder only shown when extras exist or event is past scheduled
 *
 * Raw frames sit in ONE flat pool. Mark M (main) and E (exposures), then
 * + Shot files them against whichever category the sticky bottom bar has
 * selected — the service is decided at that moment, not at upload time.
 * Drone and Dusk take no exposures, so + Shot files one main-only shot per
 * marked frame; they still land in separate editor sections for AI edit.
 *
 * IMPORTANT: We use campaignEventId as the unique service identifier (serviceId)
 * because eventTypeKey can repeat (e.g. two "photo" services with different quantities).
 */
// Print masters on THIS flow are uploaded directly, with no RAWs behind them,
// so they carry the lenient floor (MIN_PRINT_BYTES_DIRECT server-side). The
// editor flow in this same repo (useEditorPrintUpload), where the print is
// merged from RAW brackets, keeps 5MB.
//
// The server picks the tier itself from whether the event has a rawPhotos
// batch, so this cannot be loosened from the client; it exists so the
// uploader is told before a multi-MB upload rather than by a 400 after it.
const MIN_PRINT_MB = 2;

const rawFormats = [
  ".cr3",
  ".cr2",
  ".dng",
  ".nef",
  ".arw",
  ".raf",
  ".rw2",
  ".orf",
  ".pef",
  ".x-adobe-dng",
];
const SERVICE_LABELS = {
  photo: "Photo",
  photoReshoot: "Photo Reshoot",
  photoFreeShoot: "Photo Freeshoot",
  dusk: "Dusk",
  drone: "Drone",
  duskGolden: "Dusk & Golden Hour",
  portrait: "Portraits",
};
const MAX_RETRIES = 3;
const MAX_CONCURRENT = 3;
const isRawFormat = (filename) =>
  rawFormats.some((ext) => filename?.toLowerCase().endsWith(ext));
// Helper: get a unique ID for a service object
const getServiceId = (svc) => svc.campaignEventId || svc._id;
const toLocalImageId = (serverId) => `raw-${serverId}`;
const AdminServiceImageUpload = ({ eventProp, photoServices }) => {
  const deliversEdited = contractorDeliversEditedPhotos({
    contractorId: eventProp?.contractorId,
  });
  const requiredEdited = requiredEditedCount(photoServices);
  const portraitService = useMemo(
    () => ({
      eventTypeKey: "portrait",
      quantity: PORTRAIT_MAX_COUNT,
      campaignEventId: `portrait-${eventProp._id}`,
      _id: `portrait-${eventProp._id}`,
    }),
    [eventProp._id]
  );
  const uploadServices = useMemo(
    () =>
      deliversEdited
        ? sortServicesByTabOrder(photoServices || [])
        : sortServicesByTabOrder([...(photoServices || []), portraitService]),
    [photoServices, portraitService, deliversEdited]
  );

  // Use campaignEventId as the unique tab identifier (not eventTypeKey)
  const [activeServiceId, setActiveServiceId] = useState(
    getServiceId(photoServices[0]) || getServiceId(portraitService) || null
  );
  const [images, setImages] = useState([]);
  // persistShotsSnapshot runs from ShotFlow's onShotsChange, where the
  // `images` closure can be a render behind. Read through the ref instead.
  const imagesRef = useRef(images);
  imagesRef.current = images;
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [uploadProgress, setUploadProgress] = useState({});
  const [activeFolder, setActiveFolder] = useState(null);
  const [allImagesUploaded, setAllImagesUploaded] = useState(false);
  const [editConfirm, setEditConfirm] = useState(false);
  // In-flight state for the edited-photo submit. The modal button had none at
  // all (`loading` was wired to notifyEditorMutation, which this flow never
  // runs), so a second click ran the whole submit again — and because ids are
  // allocated fresh from maxId, that appends a full DUPLICATE set rather than
  // being a no-op.
  //
  // The state drives the button; the ref does the actual guarding. State is
  // not enough on its own: the submit awaits a GET (for maxId) before it
  // could set the flag, and a setState is async anyway, so two fast clicks
  // would both read `false` and both proceed. A ref flips synchronously, so
  // the second call sees it on the very next line.
  const [submittingEdited, setSubmittingEdited] = useState(false);
  const submittingEditedRef = useRef(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [notes, setNotes] = useState("");
  // Flat shot list. Every shot carries the serviceId it was filed against,
  // stamped by the category bar at the moment + Shot is pressed rather than
  // by which tab the image was uploaded under.
  const [shots, setShots] = useState([]);
  const shotsLoadedRef = useRef(false);
  const fileInputId = `file-upload-${eventProp._id}`;
  const isCompleted = [
    "completed",
    "revisionSent",
    "revisionInProgress",
    "revisionCompleted",
  ].includes(eventProp?.status);
  const { downloads, isDownloading, queueDownload, removeDownload } =
    useDownloadManager();
  // React Query mutations — invalidate adminContractorEvents on success, no page reload
  const notifyEditorMutation = useAdminNotifyEditorMutation();
  const saveImagesMutation = useAdminSaveImagesMutation();
  const createEditorShotMutation = useCreateEditorShotMutation();
  const deleteS3Mutation = useAdminDeleteS3FileMutation();
  // ─── CR3 raw image rendering (admin approach) ──────────────────
  const [processedImages, setProcessedImages] = useState(new Set());
  const [processingImages, setProcessingImages] = useState(new Set());
  const retryCountRef = useRef({});
  const MAX_RAW_RETRIES = 3;
  const rawImageMutation = useRawImageConversion({
    onSuccess: (data) => {
      setImages((prev) =>
        prev.map((img) =>
          img.id === data.imageId
            ? { ...img, originalUrl: data.originalUrl, url: data.convertedUrl }
            : img
        )
      );
      // Clear retry count on success
      delete retryCountRef.current[data.imageId];
      setProcessedImages((prev) => new Set([...prev, data.imageId]));
    },
    onError: (error, variables) => {
      const { imageId, imageUrl } = variables;
      const count = (retryCountRef.current[imageId] || 0) + 1;
      retryCountRef.current[imageId] = count;
      if (count < MAX_RAW_RETRIES) {
        console.warn(
          `RAW conversion failed (attempt ${count}/${MAX_RAW_RETRIES}), retrying:`,
          imageId
        );
        // Remove from processingImages so batch useEffect can re-pick if needed
        setProcessingImages((prev) => {
          const s = new Set(prev);
          s.delete(imageId);
          return s;
        });
        // Retry with delay
        setTimeout(() => {
          rawMutationRef.current.mutate({
            imageUrl,
            imageId,
            revisionId: null,
          });
        }, 1000 * count);
      } else {
        console.error(
          `RAW conversion failed after ${MAX_RAW_RETRIES} attempts:`,
          imageId,
          error
        );
        // Mark as processed so it won't retry infinitely
        delete retryCountRef.current[imageId];
        setProcessedImages((prev) => new Set([...prev, imageId]));
      }
    },
    onSettled: (data, error, variables) => {
      // Only remove from processingImages if not retrying
      if (!error) {
        setProcessingImages((prev) => {
          const s = new Set(prev);
          s.delete(variables.imageId);
          return s;
        });
      }
    },
  });
  // Keep mutation in ref so it never goes stale in upload callbacks
  const rawMutationRef = useRef(rawImageMutation);
  rawMutationRef.current = rawImageMutation;
  // Active service object (found by unique serviceId)
  const activeService = uploadServices.find(
    (s) => getServiceId(s) === activeServiceId
  );
  // Get editCount (shot count) for a given serviceId
  // duskGolden: quantity=4 means 2 dusk + 2 golden after duplication,
  // so admin arranges only 2 shots
  const getEditCount = (serviceId) => {
    const svc = uploadServices.find((s) => getServiceId(s) === serviceId);
    if (!svc) return 0;
    if (svc.eventTypeKey === "portrait") return PORTRAIT_MAX_COUNT;
    if (svc.eventTypeKey === "duskGolden") return Math.floor(svc.quantity / 2);
    return svc.quantity || 0;
  };
  // Shot count for active service
  // ─── Build display labels with dedup numbering ──────────────────
  // e.g. [photo, dusk, photo] → ["Photo 1", "Dusk", "Photo 2"]
  const getTabLabel = (svc) => {
    const baseLabel = SERVICE_LABELS[svc.eventTypeKey] || svc.eventTypeKey;
    const sameTypeServices = uploadServices.filter(
      (s) => s.eventTypeKey === svc.eventTypeKey
    );
    if (sameTypeServices.length <= 1) return baseLabel;
    const positionAmongSameType =
      sameTypeServices.findIndex((s) => getServiceId(s) === getServiceId(svc)) +
      1;
    return `${baseLabel} ${positionAmongSameType}`;
  };

  /**
   * The bottom bar's category list — one row per booked service, carrying the
   * quota its tab badge used to show. Labels come from getTabLabel, so an
   * event that booked two services of one type still reads Photo 1 / Photo 2
   * and keeps a separate quota for each.
   */
  const shotServices = useMemo(
    () =>
      uploadServices.map((svc) => ({
        serviceId: getServiceId(svc),
        eventTypeKey: svc.eventTypeKey,
        label: getTabLabel(svc),
        quota: getEditCount(getServiceId(svc)),
        isDirect: isDirectUploadService(svc.eventTypeKey),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uploadServices]
  );

  // One flat pool for every raw frame; the tab strip is gone.
  const showShotFlow = !deliversEdited && activeFolder === "raw";
  // ─── Upload state tracking ──────────────────────────────────────
  useEffect(() => {
    setAllImagesUploaded(
      images.length > 0 &&
        images.every((img) => img.isUploaded) &&
        // A print whose web half is still being generated is not ready: its
        // generatedWeb is what submitEditedPhotos sends as the web image.
        !images.some((img) => img.generatingWeb)
    );
  }, [images]);

  // ─── Resolve serviceId for an image ─────────────────────────────
  // Images in DB don't have serviceId. We resolve using:
  //   1. batchEventId → match service campaignEventId (handles duplicate eventTypeKeys)
  //   2. eventTypeKey → first matching service
  //   3. Fallback → first service
  const resolveServiceId = (eventTypeKey, batchEventId) => {
    if (eventTypeKey === "portrait") return getServiceId(portraitService);
    if (batchEventId) {
      const match = uploadServices.find(
        (s) => s.campaignEventId === batchEventId
      );
      if (match) return getServiceId(match);
    }
    if (eventTypeKey) {
      const match = uploadServices.find((s) => s.eventTypeKey === eventTypeKey);
      if (match) return getServiceId(match);
    }
    return getServiceId(uploadServices[0]);
  };

  // ─── Helper: process a single image into our state shape ────────
  const processRawImage = (img, id, batchEventId) => {
    let imageUrl = img.url;
    let originalUrl = img.url;
    const isRaw = rawFormats.some((ext) =>
      img.name?.toLowerCase().endsWith(ext)
    );
    if (isRaw && img.revisions?.length > 0) {
      const origRev = img.revisions.find((r) => r.isOriginal);
      if (origRev) originalUrl = origRev.url;
      if (img.activeRevision) {
        const active = img.revisions.find((r) => r._id === img.activeRevision);
        if (active) imageUrl = active.url;
      } else {
        imageUrl = img.revisions[0].url;
      }
    }
    let s3Key = img.s3Key || null;
    if (!s3Key && img.url && img.url.includes(".amazonaws.com/")) {
      s3Key = img.url.split(".amazonaws.com/")[1];
    }
    return {
      id,
      serverImageId: img.id,
      url: imageUrl,
      originalUrl,
      name: img.name || "Image",
      folder: "raw",
      isUploaded: true,
      size: img.size,
      s3Key,
      serviceId: resolveServiceId(img.eventTypeKey, batchEventId),
      eventTypeKey: img.eventTypeKey || null,
      type: img.type || null,
    };
  };

  // ─── Load existing images + shots ───────────────────────────────
  // rawPhotos can come in two shapes depending on the API:
  //   A) Nested batches: [{ images: [...], eventId, ... }, ...]
  //   B) Flat image array: [{ url, name, eventTypeKey, ... }, ...]
  // We detect by checking if the first item has an `images` array.
  useEffect(() => {
    if (eventProp.rawPhotos || eventProp.extraPhotos) {
      const rawPhotos = eventProp.rawPhotos || [];
      const isNested =
        rawPhotos.length > 0 && Array.isArray(rawPhotos[0]?.images);

      let rawImages = [];

      if (isNested) {
        // Nested batches — batch.eventId maps to service.campaignEventId
        rawImages = rawPhotos.flatMap((batch) => {
          const batchEventId =
            batch.eventId?._id || batch.eventId?.toString?.() || batch.eventId;
          return (batch.images || []).map((img) =>
            processRawImage(img, toLocalImageId(img.id), batchEventId)
          );
        });
      } else {
        // Flat array — match by eventTypeKey only
        rawImages = rawPhotos.map((img) =>
          processRawImage(img, toLocalImageId(img.id), null)
        );
      }

      const allImages = [
        ...rawImages,
        ...(eventProp.extraPhotos?.map((img, i) => ({
          id: `extra-${i + 1}`,
          serverImageId: img.id,
          url: img.url,
          name: img.name || `Image ${i + 1}`,
          folder: "extras",
          isUploaded: true,
          size: img.size,
          serviceId: resolveServiceId(img.eventTypeKey, null),
          eventTypeKey: img.eventTypeKey || null,
        })) || []),
      ];

      setImages(allImages);

      if (!shotsLoadedRef.current && eventProp.editorShots?.length) {
        shotsLoadedRef.current = true;
        // Saved shots carry eventTypeKey (normalizeShot persists it), so the
        // service is recoverable. Where an event booked two services of the
        // same key, fill them in order up to each quota — the same order the
        // bottom bar lists them in, so counts stay stable across reloads.
        const remaining = new Map(
          uploadServices.map((svc) => [
            getServiceId(svc),
            getEditCount(getServiceId(svc)),
          ])
        );
        const claimService = (eventTypeKey, mainImg) => {
          const key = eventTypeKey || mainImg?.eventTypeKey || null;
          const candidates = uploadServices.filter(
            (svc) => svc.eventTypeKey === key
          );
          const pool = candidates.length ? candidates : uploadServices;
          const open = pool.find((svc) => remaining.get(getServiceId(svc)) > 0);
          const chosen = open || pool[0];
          const id = getServiceId(chosen);
          remaining.set(id, (remaining.get(id) || 0) - 1);
          return { serviceId: id, eventTypeKey: chosen?.eventTypeKey ?? key };
        };
        const loadedShots = [];
        const defaultSvc = getServiceId(photoServices[0]);
        const mapped = eventProp.editorShots.map((shot, index) => {
          const mainLocal =
            shot.mainImageId != null
              ? toLocalImageId(shot.mainImageId)
              : null;
          const mainImg = allImages.find((img) => img.id === mainLocal);
          const serviceId = mainImg?.serviceId || defaultSvc;

          let exposureServerIds = shot.exposureImageIds || [];
          if (
            shot.category === "GOLDEN_HOUR" &&
            exposureServerIds.length === 0
          ) {
            const duskPair = eventProp.editorShots.find((candidate) => {
              if (candidate.category === "GOLDEN_HOUR") return false;
              if (shot.pairedDuskMainImageId != null) {
                return (
                  String(candidate.mainImageId) ===
                  String(shot.pairedDuskMainImageId)
                );
              }
              const duskMainLocal =
                candidate.mainImageId != null
                  ? toLocalImageId(candidate.mainImageId)
                  : null;
              const duskMainImg = allImages.find(
                (img) => img.id === duskMainLocal,
              );
              return (
                !!mainImg &&
                !!duskMainImg &&
                (mainImg.name === duskMainImg.name ||
                  mainImg.url === duskMainImg.url)
              );
            });
            exposureServerIds = duskPair?.exposureImageIds || [];
          }

          const claimed = claimService(shot.eventTypeKey, mainImg);
          const mappedShot = {
            ...createEmptyShot(index),
            ...shot,
            serviceId: claimed.serviceId || serviceId,
            eventTypeKey: claimed.eventTypeKey,
            mainImageId: mainLocal,
            exposureImageIds: exposureServerIds.map(toLocalImageId),
            pairedDuskMainImageId:
              shot.pairedDuskMainImageId != null
                ? toLocalImageId(shot.pairedDuskMainImageId)
                : null,
          };
          loadedShots.push(mappedShot);
          return mappedShot;
        });
        if (mapped.length) {
          setShots(loadedShots);
        }
      }
    }
    // Intentionally keyed on eventProp alone: getEditCount and uploadServices
    // are rebuilt every render, so listing them would re-run this on a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventProp]);

  // ─── CR3 rendering on folder open ──────────────────────────────
  useEffect(() => {
    if (!activeFolder) return;
    const render = async () => {
      const unprocessed = images.filter(
        (img) =>
          img.url &&
          !img.url.includes("blob") &&
          img.url.includes("https") &&
          img.isUploaded &&
          isRawFormat(img.name) &&
          isRawFormat(img.url) &&
          !processedImages.has(img.id) &&
          !processingImages.has(img.id)
      );
      if (unprocessed.length === 0) return;
      setProcessingImages(
        (prev) => new Set([...prev, ...unprocessed.map((i) => i.id)])
      );
      for (let i = 0; i < unprocessed.length; i += 3) {
        await Promise.all(
          unprocessed.slice(i, i + 3).map((img) =>
            rawMutationRef.current
              .mutateAsync({
                imageUrl: img.url,
                imageId: img.id,
                revisionId: null,
              })
              .catch(() => {})
          )
        );
        if (i + 3 < unprocessed.length)
          await new Promise((r) => setTimeout(r, 1000));
      }
    };
    render();
  }, [activeFolder, images, processedImages, processingImages]);

  const removeImageFromShots = (imageId) => {
    setShots((prev) =>
      prev
        .map((shot) => ({
          ...shot,
          mainImageId:
            String(shot.mainImageId) === String(imageId)
              ? null
              : shot.mainImageId,
          exposureImageIds: (shot.exposureImageIds || []).filter(
            (id) => String(id) !== String(imageId)
          ),
        }))
        .filter((shot) => shot.mainImageId != null)
    );
  };

  // Best-effort draft persistence — only succeeds once the raw photo batch
  // exists server-side (after the first "Send to Editor"); until then this
  // silently no-ops since none of the local images have a serverImageId yet.
  const persistShotsSnapshot = async (snapshotShots) => {
    const propertyId = eventProp?.propertyId?._id;
    if (!propertyId) return;

    const allLocalShots = (snapshotShots || []).filter(
      (shot) => shot.mainImageId != null
    );
    if (!allLocalShots.length) return;

    const payload = allLocalShots
      .map((shot, index) => {
        const ids = shotImageIds(shot)
          .map((localId) => {
            const img = imagesRef.current.find((i) => i.id === localId);
            return img?.serverImageId ?? null;
          })
          .filter((id) => id != null);
        if (!ids.length) return null;
        return {
          shotId: shot.shotId,
          label: shot.label || `Shot ${index + 1}`,
          mainImageId: ids[0],
          exposureImageIds: ids.slice(1),
          status: "draft",
          eventTypeKey: shot.eventTypeKey || null,
        };
      })
      .filter(Boolean);

    if (!payload.length) return;
    try {
      await createEditorShotMutation.mutateAsync({
        propertyId,
        eventId: eventProp._id,
        shots: payload,
      });
    } catch (err) {
      console.error("Failed to persist draft shots:", err);
    }
  };

  // ─── File upload (parallel — 3 concurrent) ─────────────────────
  const handleFileUpload = async (event) => {
    let pickedFiles = Array.from(event.target.files);
    event.target.value = "";
    if (pickedFiles.length === 0) return;

    // Prints below the floor can't have a web version derived from them, so
    // drop them here rather than uploading megabytes that will be rejected.
    if (activeFolder === "print") {
      const tooSmall = pickedFiles.filter(
        (f) => f.size / (1024 * 1024) < MIN_PRINT_MB
      );
      if (tooSmall.length) {
        pickedFiles = pickedFiles.filter(
          (f) => f.size / (1024 * 1024) >= MIN_PRINT_MB
        );
        showToast(
          "error",
          `Skipped ${tooSmall.length} file(s) under ${MIN_PRINT_MB}MB — ` +
            `print masters must be at least ${MIN_PRINT_MB}MB for the web ` +
            `version to be generated: ${tooSmall
              .map((f) => `${f.name} (${(f.size / (1024 * 1024)).toFixed(2)}MB)`)
              .join(", ")}`
        );
      }
      if (pickedFiles.length === 0) return;
    }

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    const retry = async (op, retries = MAX_RETRIES) => {
      for (let i = 1; i <= retries; i++) {
        try {
          return await op();
        } catch (e) {
          if (i === retries) throw e;
          await sleep(1000);
        }
      }
    };
    const newFiles = pickedFiles.map((file, i) => ({
      id: Date.now() + i,
      file,
      url: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
      folder: activeFolder,
      isUploaded: false,
      s3Key: null,
      eventId: eventProp._id,
      // Tag uploaded images with the currently active service tab
      serviceId: activeServiceId,
      eventTypeKey: activeService?.eventTypeKey || null,
      // A print's web half is derived once the upload finishes; the flag keeps
      // Submit disabled until it resolves either way.
      generatingWeb: activeFolder === "print",
      generatedWeb: null,
    }));
    setImages((prev) => [...prev, ...newFiles]);

    // Capture the folder at upload time so a mid-upload folder switch can't
    // send the follow-up work down the wrong branch.
    const folderAtUpload = activeFolder;

    // Upload single file (used by worker pool below)
    const uploadOne = async (f) => {
      try {
        await retry(async () => {
          const res = await axios.post("/admin/prepareMarketing/fileUpload", {
            files: [{ fileName: f.name, fileType: f.file.type }],
            propertyId: eventProp?.propertyId?._id,
            folderPath: folderField(activeFolder),
          });
          const presigned = res.data.presignedUrls[0];
          await axios.put(presigned.url, f.file, {
            headers: { "Content-Type": f.file.type },
            onUploadProgress: (e) => {
              setUploadProgress((prev) => ({
                ...prev,
                [f.id]: Math.round((e.loaded * 100) / e.total),
              }));
            },
          });
          const s3Url = presigned.url.split("?")[0];
          // Update url from blob → s3Url so RAW batch filter picks it up
          // Clear file reference to free memory (RAW files can be 20-50MB each)
          setImages((prev) =>
            prev.map((img) =>
              img.id === f.id
                ? {
                    ...img,
                    isUploaded: true,
                    s3Key: presigned.key,
                    s3Url,
                    url: s3Url,
                    file: null,
                  }
                : img
            )
          );
          // Free blob URL — no longer needed since url is now s3Url
          URL.revokeObjectURL(f.url);
          // Clean up upload progress entry for this file
          setUploadProgress((prev) => {
            const { [f.id]: removed, ...rest } = prev;
            return rest;
          });

          // Print masters carry their web half: the uploader supplies print
          // only and the web derivative is generated server-side, so the pair
          // exists before Submit and nothing has to be matched by filename.
          // Own try/catch — a generation failure must not retry the upload.
          if (folderAtUpload === "print") {
            try {
              const genRes = await axios.post(
                "/admin/prepareMarketing/generateWebFromPrint",
                {
                  propertyId: eventProp?.propertyId?._id,
                  eventId: eventProp._id,
                  printS3Key: presigned.key,
                  fileNameHint: f.name,
                }
              );
              const web = genRes.data?.data?.web;
              if (!genRes.data?.success || !web?.s3Key) {
                throw new Error(
                  genRes.data?.message || "No web image returned"
                );
              }
              setImages((prev) =>
                prev.map((img) =>
                  img.id === f.id
                    ? { ...img, generatedWeb: web, generatingWeb: false }
                    : img
                )
              );
            } catch (genErr) {
              console.error("Web generation failed:", f.name, genErr);
              setImages((prev) =>
                prev.map((img) =>
                  img.id === f.id ? { ...img, generatingWeb: false } : img
                )
              );
              showToast(
                "error",
                genErr?.response?.data?.message ||
                  genErr?.message ||
                  `Couldn't generate the web version of ${f.name}. Delete it and re-upload.`
              );
            }
          }
          // Trigger RAW conversion directly
          if (isRawFormat(f.name)) {
            rawMutationRef.current.mutate({
              imageUrl: s3Url,
              imageId: f.id,
              revisionId: null,
            });
          }
        });
      } catch (error) {
        console.error(`Failed to upload ${f.name}:`, error);
      }
    };
    // Parallel upload — 3 files at a time via worker pool
    const queue = [...newFiles];
    const runWorker = async () => {
      while (queue.length > 0) {
        const file = queue.shift();
        if (file) await uploadOne(file);
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(MAX_CONCURRENT, newFiles.length) }, () =>
        runWorker()
      )
    );
  };
  // ─── Delete ─────────────────────────────────────────────────────
  const deleteImage = async (imageId) => {
    const image = images.find((img) => img.id === imageId);
    if (!image) return;
    setImages((prev) => prev.filter((img) => img.id !== imageId));
    removeImageFromShots(imageId);
    deleteS3Mutation.mutate({
      key: image?.s3Key,
      name: image.name,
      eventId: eventProp?._id,
      propertyId: eventProp?.propertyId?._id,
      field: activeFolder === "raw" ? "rawPhotos" : "extraPhotos",
    });
  };
  const deleteAllImages = async () => {
    // Only delete images in the current folder AND current service tab
    const toDelete = images.filter(
      (img) =>
        img.folder === activeFolder &&
        img.serviceId === activeServiceId &&
        ((img.isUploaded && img.s3Key) || img.url)
    );
    // The pool is flat, so "delete all" clears the folder rather than the
    // slice that happened to belong to the active tab.
    const keepImages = images.filter((img) => img.folder !== activeFolder);
    setImages(keepImages);
    toDelete.forEach((img) => removeImageFromShots(img.id));
    if (activeFolder === "raw") setShots([]);
    await Promise.all(
      toDelete.map((img) =>
        deleteS3Mutation
          .mutateAsync({
            key: img.s3Key,
            name: img.name,
            eventId: eventProp?._id,
            propertyId: eventProp?.propertyId?._id,
            field: activeFolder === "raw" ? "rawPhotos" : "extraPhotos",
          })
          .catch(() => {})
      )
    );
  };
  // ─── Image filtering ───────────────────────────────────────────
  // Images for the current folder AND current service (by serviceId)
  // Flat pool: every raw frame for the folder, whatever service it ends up
  // filed against. The shot decides the service now, not the upload.
  const getCurrentImages = () =>
    images.filter((img) => img.folder === activeFolder);

  // Direct-upload services (drone / dusk) now go through + Shot as well —
  // one main-only shot per image — so shots are the single source of what is
  // assigned, and anything left over is genuinely an extra.
  const getAssignedImageIds = () => {
    const ids = new Set();
    shots.forEach((shot) => {
      shotImageIds(shot).forEach((id) => ids.add(String(id)));
    });
    return ids;
  };

  // ─── Validate all tabs before send ──────────────────────────────
  const validateAllServices = () => {
    const errors = [];
    for (const svc of uploadServices) {
      const svcId = getServiceId(svc);
      const label = SERVICE_LABELS[svc.eventTypeKey] || svc.eventTypeKey;
      const required = getEditCount(svcId);
      // Quotas are counted off the shots filed against each service, since a
      // frame no longer belongs to a service until + Shot puts it in one.
      const svcShots = shots.filter((s) => s.serviceId === svcId);

      if (isDirectUploadService(svc.eventTypeKey)) {
        if (svcShots.length < required) {
          errors.push(
            `${label}: assign ${required} image(s) (have ${svcShots.length})`
          );
        }
        if (svcShots.length > required) {
          errors.push(
            `${label}: too many images (${svcShots.length}/${required})`
          );
        }
        continue;
      }

      const withMain = svcShots.filter((s) => s.mainImageId != null);
      if (svc.eventTypeKey === "portrait") {
        if (withMain.length > PORTRAIT_MAX_COUNT) {
          errors.push(
            `${label}: max ${PORTRAIT_MAX_COUNT} portraits (have ${withMain.length})`
          );
        }
        continue;
      }
      if (withMain.length < required) {
        errors.push(
          `${label}: arrange ${required} shot(s) with a main (have ${withMain.length})`
        );
      }
      if (withMain.length > required) {
        errors.push(
          `${label}: too many shots (${withMain.length}/${required})`
        );
      }
    }
    return errors;
  };

  // Prints picked in THIS session. `serverImageId` marks the ones loaded back
  // from prepareMarketing, which are already delivered: they carry no
  // generatedWeb (their web half is already on the marketing doc), and the
  // ids below are allocated fresh from maxId, so re-sending one would append
  // a DUPLICATE image rather than touch the original.
  const newEditedPrints = images.filter(
    (img) =>
      img.folder === "print" &&
      img.isUploaded &&
      img.s3Key &&
      !img.serverImageId
  );

  const runEditedPhotoSubmit = async () => {
    // The job's photo quantity is guidance, not a gate — it used to demand
    // exactly `requiredEdited` web AND print files, which rejected
    // over-delivery as loudly as under-delivery. Nothing enforces the count
    // server-side either (updatePrepareMarketing never reads
    // services[].quantity, and the event status is PUT separately), so what
    // is checked now is only what would actually break the delivery.
    const print = newEditedPrints;
    if (!print.length) {
      showToast("error", "Upload at least one print image before submitting.");
      return;
    }
    // Exactly the number the job was sold as — no fewer, no more. The server
    // enforces the same rule on the completion request
    // (IMAGE_COUNT_MISMATCH), counting distinct image ids.
    if (requiredEdited > 0 && print.length !== requiredEdited) {
      showToast(
        "error",
        print.length > requiredEdited
          ? `This job is for exactly ${requiredEdited} image(s) — remove ${print.length - requiredEdited}.`
          : `This job is for exactly ${requiredEdited} image(s) — ${requiredEdited - print.length} still to upload.`
      );
      return;
    }
    if (print.some((img) => img.generatingWeb)) {
      showToast(
        "error",
        "Still generating web versions — wait for them to finish."
      );
      return;
    }
    const missingWeb = print.filter((img) => !img.generatedWeb?.s3Key);
    if (missingWeb.length) {
      showToast(
        "error",
        `${missingWeb.length} print file(s) have no web version. Delete and re-upload: ${missingWeb
          .map((img) => img.name)
          .join(", ")}`
      );
      return;
    }
    const propertyId = eventProp?.propertyId?._id;
    let maxId = 0;
    try {
      const res = await axios.get(`/admin/prepareMarketing/${propertyId}`);
      const marketing = res.data?.data;
      const ids = [
        ...(marketing?.editedWebPhotos || []),
        ...(marketing?.editedPrintPhotos || []),
      ]
        .map((p) => Number(p.id))
        .filter((n) => Number.isFinite(n));
      maxId = ids.length ? Math.max(...ids) : 0;
    } catch {
      maxId = 0;
    }
    // One id per print, shared with the web generated from it — they pair by
    // construction, so nothing infers a pairing from filenames and neither
    // half can be silently dropped.
    const editedPrintPhotos = print.map((img, index) => ({
      id: maxId + index + 1,
      name: img.name,
      size: img.size,
      s3Key: img.s3Key,
      eventId: eventProp._id,
    }));
    const editedWebPhotos = print.map((img, index) => ({
      id: maxId + index + 1,
      // "-web.jpg" is what the editor flow persists, and photoSceneKey strips
      // the suffix, so the stem still matches the print it came from.
      name: `${(img.name || "image").replace(/\.[^.]+$/, "")}-web.jpg`,
      size: img.generatedWeb.size,
      s3Key: img.generatedWeb.s3Key,
      eventId: eventProp._id,
    }));
    try {
      await axios.put(`/admin/prepareMarketing/${propertyId}`, {
        // persistOnly keeps the server's rawPhotos-batch branch (and its
        // revision-completed emails) out of the way — a self-edit job has no
        // batch. completeEvent is what flips the event, exactly as the
        // services app now does it, and the server DERIVES the value:
        // revisionCompleted when answering a revision request, completed
        // otherwise. It is also the gate for the image-count quota.
        persistOnly: true,
        completeEvent: true,
        eventId: eventProp._id,
        editedWebPhotos,
        editedPrintPhotos,
      });
      showToast("success", "Photos submitted and job marked complete");
      setEditConfirm(false);
    } catch (err) {
      console.error(err);
      showToast(
        "error",
        err?.response?.data?.message || "Failed to submit photos"
      );
    }
  };

  const submitEditedPhotos = async () => {
    if (submittingEditedRef.current) return;
    submittingEditedRef.current = true;
    setSubmittingEdited(true);
    try {
      await runEditedPhotoSubmit();
    } finally {
      submittingEditedRef.current = false;
      setSubmittingEdited(false);
    }
  };

  // ─── Send to editor (one batch for all services) ───────────────
  const notifyEditor = () => {
    const assignedIds = getAssignedImageIds();

    // Direct services now produce shots too (one main-only shot per image),
    // so both kinds collapse into one pass. The wire format is unchanged:
    // ordered s3Keys with the main first, which is how the backend derives M/E.
    const allShots = [];
    shots
      .filter((shot) => shot.mainImageId != null)
      .forEach((shot) => {
        const keys = shotImageIds(shot)
          .map((localId) => {
            const img = images.find((i) => i.id === localId);
            return img?.s3Key || null;
          })
          .filter(Boolean);
        if (!keys.length) return;
        allShots.push({
          shotId: shot.shotId,
          label: shot.label || `Shot ${allShots.length + 1}`,
          s3Keys: keys,
          eventTypeKey: shot.eventTypeKey,
        });
      });

    // Raw frames no longer carry a service from upload time — the shot they
    // landed in does. Backfill from it so the payload the backend receives is
    // byte-for-byte what the tabbed flow used to send.
    const serviceByImageId = new Map();
    shots.forEach((shot) => {
      shotImageIds(shot).forEach((id) =>
        serviceByImageId.set(String(id), {
          eventTypeKey: shot.eventTypeKey || null,
          serviceId: shot.serviceId || null,
        })
      );
    });

    // Collect all shot-assigned raw images across all service tabs
    const rawImgs = images
      .filter(
        (img) =>
          img.isUploaded &&
          img.folder === "raw" &&
          img.s3Key &&
          assignedIds.has(String(img.id))
      )
      .map((img) => {
        const from = serviceByImageId.get(String(img.id)) || {};
        const eventTypeKey = from.eventTypeKey ?? img.eventTypeKey ?? null;
        return {
          s3Key: img.s3Key,
          name: img.name,
          size: img.size,
          eventTypeKey,
          serviceId: from.serviceId ?? img.serviceId ?? null,
          type: eventTypeKey === "portrait" ? "P" : undefined,
        };
      });
    // Extras = uploaded raw images NOT assigned to a shot
    const extraImgs = images
      .filter(
        (img) =>
          img.isUploaded &&
          img.folder === "raw" &&
          img.s3Key &&
          !assignedIds.has(String(img.id))
      )
      .map((img) => ({
        s3Key: img.s3Key,
        name: img.name,
        size: img.size,
        eventTypeKey: img.eventTypeKey,
        serviceId: img.serviceId,
      }));
    notifyEditorMutation.mutate(
      {
        event: eventProp,
        images: rawImgs,
        notes,
        isExtras: extraImgs.length > 0,
        extraImages: extraImgs,
        shots: allShots,
      },
      {
        onSuccess: () => {
          showToast("success", "Sent to editor successfully");
          setEditConfirm(false);
        },
        onError: (error) => console.error(error.message),
      }
    );
  };
  // ─── Save extras ───────────────────────────────────────────────
  const handleExtras = () => {
    const finalImages = images
      .filter(
        (img) => img.isUploaded && img.folder === activeFolder && img.s3Key
      )
      .map((img) => ({
        s3Key: img.s3Key,
        name: img.name,
        size: img.size,
        eventTypeKey: img.eventTypeKey,
        serviceId: img.serviceId,
      }));
    if (finalImages.length === 0) return;
    saveImagesMutation.mutate(
      {
        propertyId: eventProp?.propertyId?._id,
        event: eventProp,
        images: finalImages,
        folderPath: activeFolder === "raw" ? "rawPhotos" : "extraPhotos",
      },
      {
        onSuccess: (data) => {
          if (data.success) showToast("success", "Save successfully");
        },
      }
    );
  };
  // ─── Per-tab counts for tab badges ──────────────────────────────
  const getTabCounts = (serviceId) => {
    const svc = uploadServices.find((s) => getServiceId(s) === serviceId);
    const tabImages = images.filter(
      (img) => img.serviceId === serviceId && img.folder === "raw"
    );
    const required = getEditCount(serviceId);
    const total = tabImages.length;
    if (isDirectUploadService(svc?.eventTypeKey)) {
      return { shotCount: total, mainCount: total, total, required };
    }
    const shotCount = shots.filter(
      (s) => s.serviceId === serviceId && s.mainImageId != null
    ).length;
    return { shotCount, mainCount: shotCount, total, required };
  };
  const currentImages = getCurrentImages();
  // ─── Render ─────────────────────────────────────────────────────
  return (
    <>
      {/* Folder Navigation */}
      {!activeFolder ? (
        <div className="flex gap-3">
          {(deliversEdited
            ? // Print only — web is derived from it server-side. The web
              // folder still appears when this job already HAS web files, so
              // an earlier delivery stays viewable; it takes no new uploads.
              images.some((img) => img.folder === "web")
              ? ["print", "web", "extras"]
              : ["print", "extras"]
            : ["raw", "extras"]
          ).map((folder) => (
          <div
            key={folder}
            className="cursor-pointer flex flex-col items-center gap-1.5 bg-lightergray py-3 px-6 rounded-lg hover:bg-lightgray transition-colors border border-mediumgray"
            onClick={() => setActiveFolder(folder)}
          >
            <FiFolderPlus className="w-8 h-8 text-darkergray" />
            <p className="text-xs font-semibold text-black capitalize">{folder}</p>
          </div>
          ))}
        </div>
      ) : (
        <div>
          {/* Toolbar */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setActiveFolder(null)}
                className="gray-button flex items-center"
              >
                <FaArrowLeft className="mr-1.5" /> Back
              </Button>
              <span className="text-xs text-darkergray font-medium flex items-center">
                <HiChevronRight className="mx-1 w-3 h-3" /> {activeFolder}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {activeFolder === "raw" && (
                <button
                  type="button"
                  onClick={() => setHelpOpen(true)}
                  className="text-xs font-semibold text-darkergray hover:text-black border-0 bg-transparent cursor-pointer px-2 py-1"
                >
                  How it works
                </button>
              )}
              {(eventProp?.status === "scheduled" ||
                activeFolder !== "raw") &&
                !(deliversEdited && activeFolder === "web") && (
                <>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    id={fileInputId}
                  />
                  <label
                    htmlFor={fileInputId}
                    className="flex items-center black-button cursor-pointer"
                  >
                    <FaFileUpload className="mr-1.5" /> Upload
                  </label>
                </>
              )}
              {currentImages.length > 0 &&
                (eventProp?.status === "scheduled" ||
                  activeFolder !== "raw") && (
                  <Button className="outline-button" onClick={deleteAllImages}>
                    Delete All
                  </Button>
                )}
            </div>
          </div>
          {deliversEdited && activeFolder === "print" && (
            <div className="text-xs text-darkergray mb-3">
              Upload print masters (already edited, {MIN_PRINT_MB}MB or
              larger). The web version of each is generated automatically —
              there is no separate web upload.
              {requiredEdited ? ` This job includes ${requiredEdited}.` : ""}
            </div>
          )}
          {deliversEdited && activeFolder === "web" && (
            <div className="text-xs text-darkergray mb-3">
              Web images are generated from the print masters — this folder is
              read-only.
            </div>
          )}
          {/* Download bar */}
          {isCompleted && currentImages.length > 0 && (
            <div className="flex items-center justify-end mb-3">
              <Button
                onClick={() =>
                  queueDownload(
                    currentImages.map((img) => ({
                      ...img,
                      url: img.originalUrl || img.url,
                    })),
                    `${activeFolder}-${eventProp?.propertyId?.address || "photos"}-${eventProp?.summary}`
                  )
                }
                className="black-button flex items-center"
                disabled={isDownloading}
              >
                {isDownloading ? "Downloading..." : "Download All"}
              </Button>
            </div>
          )}
          {/* Flat pool + sticky bottom bar (category, + Shot, Send). */}
          {showShotFlow && (
            <ShotFlow
              images={currentImages.filter((img) => img.isUploaded)}
              services={shotServices}
              shots={shots}
              activeServiceId={activeServiceId}
              onActiveServiceChange={setActiveServiceId}
              readOnly={eventProp?.status !== "scheduled"}
              onDelete={
                eventProp?.status === "scheduled" ? deleteImage : undefined
              }
              onViewLarge={(image) => {
                const idx = currentImages.findIndex(
                  (img) => String(img.id) === String(image.id)
                );
                if (idx >= 0) setLightboxIndex(idx);
              }}
              onShotsChange={(nextShots) => {
                setShots(nextShots);
                persistShotsSnapshot(nextShots);
              }}
              notes={notes}
              onNotesChange={setNotes}
              sending={notifyEditorMutation.isPending}
              sendDisabled={!allImagesUploaded}
              sendLabel={allImagesUploaded ? "Send to Editor" : "Uploading…"}
              onSend={() => {
                const assignedIds = getAssignedImageIds();
                if (assignedIds.size === 0) {
                  showToast("error", "Please arrange images into shots");
                  return;
                }
                const errors = validateAllServices();
                if (errors.length > 0) {
                  showToast("error", errors[0]);
                  return;
                }
                setEditConfirm(true);
              }}
            />
          )}

          {/* Notes — the raw flow's textarea is inside ShotFlow's bottom bar. */}
          {!showShotFlow && (
            <div className="mb-3">
              <textarea
                className="form-textarea border border-mediumgray"
                rows={2}
                placeholder="Notes for editor..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          )}
          {/* Image grid — extras / edited folders only; raw uses ShotFlow. */}
          {!showShotFlow && (
            <div className="grid gap-3 mb-4 grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {currentImages.map((image, index) => (
                <ImageContainer
                  key={image.id}
                  image={image}
                  onDelete={(id) => deleteImage(id)}
                  onPreview={() => setLightboxIndex(index)}
                  viewMode="grid"
                  progress={uploadProgress[image.id] || 0}
                  showDelete={
                    !(
                      eventProp?.status !== "scheduled" &&
                      activeFolder === "raw"
                    )
                  }
                  showDownload={isCompleted}
                  showTypeSelect={false}
                  isSelected={getAssignedImageIds().has(String(image.id))}
                  eventId={eventProp?._id}
                  propertyId={eventProp?.propertyId?._id}
                />
              ))}
            </div>
          )}
          {/* Still-uploading thumbs — ShotFlow only shows uploaded frames. */}
          {showShotFlow &&
            currentImages.some((img) => !img.isUploaded) && (
              <div className="grid gap-3 mb-4 grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                {currentImages
                  .filter((img) => !img.isUploaded)
                  .map((image) => {
                    const index = currentImages.findIndex(
                      (img) => img.id === image.id,
                    );
                    return (
                      <ImageContainer
                        key={image.id}
                        image={image}
                        onDelete={(id) => deleteImage(id)}
                        onPreview={() => setLightboxIndex(index)}
                        viewMode="grid"
                        progress={uploadProgress[image.id] || 0}
                        showDelete
                        showDownload={false}
                        showTypeSelect={false}
                        eventId={eventProp?._id}
                        propertyId={eventProp?.propertyId?._id}
                      />
                    );
                  })}
              </div>
            )}
          {/* Send / Save */}
          {!(deliversEdited && activeFolder === "web") &&
            (eventProp?.status === "scheduled" || activeFolder === "extras") &&
            (currentImages.length > 0 ||
              (deliversEdited &&
                images.some(
                  (img) => img.folder === "print" && img.isUploaded
                )) ||
              (activeFolder === "raw" &&
                images.some(
                  (img) => img.folder === "raw" && img.isUploaded
                ))) && (
              <div className="flex justify-end">
                {deliversEdited && activeFolder === "print" ? (
                  <Button
                    onClick={() => {
                      if (!newEditedPrints.length) {
                        showToast(
                          "error",
                          "Upload at least one print image before submitting."
                        );
                        return;
                      }
                      // Same exact-count rule as submitEditedPhotos, checked
                      // here so the confirm modal never opens on a set that
                      // the submit would reject.
                      if (
                        requiredEdited > 0 &&
                        newEditedPrints.length !== requiredEdited
                      ) {
                        showToast(
                          "error",
                          newEditedPrints.length > requiredEdited
                            ? `This job is for exactly ${requiredEdited} image(s) — remove ${newEditedPrints.length - requiredEdited}.`
                            : `This job is for exactly ${requiredEdited} image(s) — ${requiredEdited - newEditedPrints.length} still to upload.`
                        );
                        return;
                      }
                      if (newEditedPrints.some((img) => img.generatingWeb)) {
                        showToast(
                          "error",
                          "Still generating web versions — wait for them to finish."
                        );
                        return;
                      }
                      setEditConfirm(true);
                    }}
                    className="black-button flex"
                    disabled={!allImagesUploaded}
                  >
                    {!allImagesUploaded ? (
                      <>
                        <i className="fa-solid fa-spinner animate-spin mr-2" />{" "}
                        {images.some((img) => img.generatingWeb)
                          ? "Generating web"
                          : "Uploading"}
                      </>
                    ) : (
                      "Submit photos"
                    )}
                  </Button>
                ) : activeFolder === "raw" ? (
                  <Button
                    onClick={() => {
                      const assignedIds = getAssignedImageIds();
                      const allSelected = images.filter(
                        (img) =>
                          img.folder === "raw" &&
                          assignedIds.has(String(img.id))
                      );
                      if (allSelected.length === 0) {
                        showToast("error", "Please arrange images into shots");
                        return;
                      }
                      const errors = validateAllServices();
                      if (errors.length > 0) {
                        showToast("error", errors[0]);
                        return;
                      }
                      setEditConfirm(true);
                    }}
                    className="black-button flex"
                    disabled={!allImagesUploaded}
                  >
                    {!allImagesUploaded ? (
                      <>
                        <i className="fa-solid fa-spinner animate-spin mr-2" />{" "}
                        Uploading
                      </>
                    ) : (
                      "Send to Editor"
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={handleExtras}
                    className="black-button flex"
                    disabled={!allImagesUploaded}
                    loading={saveImagesMutation.isPending}
                  >
                    {!allImagesUploaded ? (
                      <>
                        <i className="fa-solid fa-spinner animate-spin mr-2" />{" "}
                        Uploading
                      </>
                    ) : (
                      "Save"
                    )}
                  </Button>
                )}
              </div>
            )}
        </div>
      )}
      <DownloadProgressToast downloads={downloads} onRemove={removeDownload} />
      <Lightbox
        images={currentImages}
        currentIndex={lightboxIndex}
        isOpen={lightboxIndex >= 0}
        onClose={() => setLightboxIndex(-1)}
      />
      {helpOpen && (
        <HelpGuide mode="contractor" onClose={() => setHelpOpen(false)} />
      )}
      <Modal
        isOpen={editConfirm}
        onClose={() => setEditConfirm(false)}
        title={deliversEdited ? "Submit completed photos" : "Send to Editor"}
      >
        <div className="flex flex-col space-y-4">
          {/* This modal serves BOTH flows and used to render only the shot
              summary. Shots exist solely in the raw/editor flow, so a
              print/web delivery always read "0/20 shots" with a red ✗ —
              alarming, and describing work this flow never does (no HDR
              merge, no editor, no AI). Branch on the flow instead. */}
          {deliversEdited ? (
            <>
              <p className="text-sm text-darkergray">
                These files go straight to the listing as web and print. They
                will not be sent to the editor or AI.
              </p>
              <div className="border border-mediumgray rounded-xl p-3 text-xs space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Print</span>
                  <span className="text-darkergray">
                    {newEditedPrints.length} uploaded
                    {requiredEdited ? ` of ${requiredEdited} expected` : ""}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Web</span>
                  <span className="text-darkergray">
                    {
                      newEditedPrints.filter((img) => img.generatedWeb?.s3Key)
                        .length
                    }{" "}
                    generated
                  </span>
                </div>
              </div>
            </>
          ) : (
            <>
          <p className="text-sm text-darkergray">
            Once sent this cannot be undone. HDR merge will run automatically in
            the background for bracketed shots.
          </p>
          <div className="border border-mediumgray rounded-xl p-3 text-xs space-y-2">
            {uploadServices.map((svc) => {
              const serviceId = getServiceId(svc);
              const counts = getTabCounts(serviceId);
              const label = getTabLabel(svc);
              const unit = isDirectUploadService(svc.eventTypeKey)
                ? "images"
                : "shots";
              const ok =
                svc.eventTypeKey === "portrait"
                  ? counts.shotCount <= counts.required
                  : counts.shotCount === counts.required;
              return (
                <div
                  key={serviceId}
                  className="flex items-center justify-between"
                >
                  <span className="font-medium">{label}</span>
                  <span className="text-darkergray">
                    {counts.shotCount}/{counts.required} {unit}
                    {ok ? (
                      <span className="text-green-600 ml-1">✓</span>
                    ) : (
                      <span className="text-red-600 ml-1">✗</span>
                    )}
                  </span>
                </div>
              );
            })}
            <div className="border-t border-mediumgray pt-2 flex items-center justify-between text-darkergray">
              <span>Extras (unassigned)</span>
              <span>
                {
                  images.filter(
                    (img) =>
                      img.folder === "raw" &&
                      img.isUploaded &&
                      !getAssignedImageIds().has(String(img.id)),
                  ).length
                }{" "}
                images
              </span>
            </div>
          </div>
            </>
          )}
          <div className="w-full flex gap-2 justify-end">
            <Button
              onClick={() => setEditConfirm(false)}
              className="gray-button"
            >
              Cancel
            </Button>
            <Button
              onClick={deliversEdited ? submitEditedPhotos : notifyEditor}
              className="black-button"
              loading={
                deliversEdited
                  ? submittingEdited
                  : notifyEditorMutation.isPending
              }
              disabled={
                deliversEdited
                  ? submittingEdited
                  : notifyEditorMutation.isPending
              }
            >
              {deliversEdited ? "Submit photos" : "Send to Editor"}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
export default AdminServiceImageUpload;
